import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 91浏览器官方教程 | 91浏览器`;
    } else {
      document.title = '文章未找到 - 91浏览器 | 91浏览器';
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article);

  const faqContent = article.categorySlug === 'faq';
  const faqMatches = faqContent
    ? article.content.match(/<h3>Q:.*?<\/h3>\s*<p>A:.*?<\/p>/gs) || []
    : [];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors">
                {article.category}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
            </nav>
          </div>
        </div>

        <article className="py-12 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-lg text-xs font-medium mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}阅读
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 bg-background-100 text-foreground-600 rounded-md text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden h-64 md:h-80 mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 91浏览器`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-700 leading-relaxed
                prose-h2:font-heading prose-h2:text-2xl prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-10 prose-h2:mb-4
                prose-h3:font-heading prose-h3:text-xl prose-h3:font-semibold prose-h3:text-foreground-800 prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{article.category}分类
                </Link>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关文章推荐</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      onClick={() => window.scrollTo(0, 0)}
                      className="group bg-background-100 rounded-xl p-5 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                    >
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{ra.excerpt}</p>
                      <span className="text-xs text-foreground-400">{ra.date}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
      {faqContent && faqMatches.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqMatches.map((match) => {
                const qMatch = match.match(/<h3>Q:\s*(.+?)<\/h3>/);
                const aMatch = match.match(/<p>A:\s*(.+?)<\/p>/);
                return {
                  '@type': 'Question',
                  name: qMatch ? qMatch[1] : '',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: aMatch ? aMatch[1] : '',
                  },
                };
              }),
            }),
          }}
        />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Organization',
              name: article.author,
            },
            datePublished: article.date,
            image: article.imageUrl,
            publisher: {
              '@type': 'Organization',
              name: '91浏览器',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.91browser.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.category,
                item: `https://www.91browser.com/category/${article.categorySlug}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}